// 1:1 wrappers around the Withings webservice account/session endpoints.
//
// Most methods don't catch auth errors — @platform/wiservice auto-redirects
// on 103/239/250/501 before the promise rejects.
//
// `sessionIsVerified` is the exception: sets `onAuthInterrupt` so the
// package does NOT auto-redirect; the caller (the `fetchCurrentUser`
// action) inspects the WsError + branches per host.

import { ws } from '@/lib/wiservice';
import type { WsResponse } from '@/lib/wiservice';

export interface AccountData {
  accountid: string | number;
  email: string;
  firstname?: string;
  lastname?: string;
}

export interface UserData {
  // account.getuserslist returns the Withings user id as `id`; `userid` is a
  // legacy alias that may be absent. Read `id` first (see PatientNotes authorid).
  id?: string | number;
  userid?: string | number;
  email?: string;
  firstname?: string;
  lastname?: string;
  shortname?: string;
}

interface SessionVerifiedBody {
  userid: string | number;
  accountid: string | number;
  verified?: boolean;
  ttl?: number;
}

interface AccountGetBody {
  account: AccountData;
  users?: UserData[];
}

interface GetUsersListBody {
  users: UserData[];
}

interface SessionExtendBody {
  /** TTL the server actually GRANTED (seconds) — the keep-alive schedule anchor. */
  ttl?: number | string;
}

export async function get(
  params: Record<string, string | number> = {},
): Promise<AccountGetBody> {
  const response = await ws<AccountGetBody>('account', 'get', {
    applitype: 20,
    enrich: 't',
    ...params,
  });
  return response.body;
}

export async function getUsersList(
  params: Record<string, string | number> = {},
): Promise<UserData[]> {
  const response = await ws<GetUsersListBody>('account', 'getuserslist', params, true);
  return response.body.users ?? [];
}

export async function sessionIsVerified(): Promise<WsResponse<SessionVerifiedBody>> {
  return ws<SessionVerifiedBody>('session', 'isverified', {}, true, {
    onAuthInterrupt: () => {
      // Side-effect-only; we re-throw via WsError inside ws() so the
      // caller's try/catch fires (per-host branching).
    },
  });
}

/** Always pass `ttl` — apiv2 floors an extend sent without one at 300 s. */
export async function sessionExtend(
  params: Record<string, string | number> = {},
): Promise<SessionExtendBody> {
  const response = await ws<SessionExtendBody>('session', 'extend', params, true);
  return response.body;
}
